import React, { PureComponent } from 'react';
import { createSelector } from 'reselect';
import { pie, arc, ascending, format, hcl, select } from 'd3';
import './BreakdownPie.css'

const weightFormat = format('.2%')

const arcGenerator = createSelector(
  (props, state) => Math.min(state.width, state.height) / 2,
  radius => arc()
    .innerRadius((radius - 10) * .6)
    .outerRadius(radius - 10)
    .startAngle(0)
)
const pieSlices = createSelector(
  arcGenerator,
  props => props.data,
  props => props.weightField,
  (arcGenerator, data, weightField) =>
    pie()
      .value( d => d[weightField] )
      .sort((a, b) => 
        ascending(!!a.sortLast, !!b.sortLast) ||
        ascending(b[weightField], a[weightField])
      )(data)
        .map(d => ({
          ...d,
          path: arcGenerator.startAngle(d.startAngle).endAngle(d.endAngle)()
        }))
)
const hoveredSlice = createSelector(
  pieSlices,
  props => props.highlighted,
  props => props.nameField,
  (slices, highlighted, nameField) => slices.find(d => d.data[nameField] === highlighted)
)
const hoveredSliceAnchor = createSelector(
  hoveredSlice,
  arcGenerator,
  (hovered, arcGenerator) => {
    if (!hovered) return [0, 0]
    const angle = (hovered.startAngle + hovered.endAngle) / 2
    const radius = arcGenerator.outerRadius()() + 5
    return [
      radius * Math.sin(angle), 
      -radius * Math.cos(angle)
    ]
  }
)

class BreakdownPie extends PureComponent {
  constructor() {
    super()
    this.state = {
      tooltipWidth:  0,
      tooltipHeight: 0,
      width: null,
      resizeListenerId: 'resize.' + String(Math.random()).substr(2, 5)
    }
  }
  componentDidMount() {
    this.updateWidth()
    select(window).on(this.state.resizeListenerId, () => this.updateWidth() ) 
  }
  updateWidth() {
    const container = this.refs.container
    const width = container.getBoundingClientRect().width
    this.setState({
      width, height: width
    })
  }
  componentDidUpdate() {
    // bail if tooltip isn't yet in the DOM (see render() for cause)
    if (!this.refs.tooltip) { return }
      
    // measure the tooltip
    const tooltipBounds = this.refs.tooltip.getBoundingClientRect()
    this.setState({
      tooltipWidth:  tooltipBounds.width,
      tooltipHeight: tooltipBounds.height
    })
  }
  componentWillReceiveProps(nextProps) {
    if (nextProps.width !== this.props.width) {
      this.updateWidth()
    }
  }
  render() {
    const { 
      onSelect, onHighlight, colorScale, percentTopN, numTopN, innerLabel,
      nameField, weightField, weightOfTopLabel, splitInnerLabelIfLongerThan
    } = this.props
    const len = splitInnerLabelIfLongerThan
    const { width, height, tooltipWidth, tooltipHeight } = this.state
    const hovered = hoveredSlice(this.props, this.state)
    const tooltipPos = hoveredSliceAnchor(this.props, this.state)
    const bgColor = hovered ? colorScale(hovered.data[nameField]) : null

    if (!width) { return <div ref='container' className='BreakdownPie dc-col-sm-6' /> }


    return (
      <div ref='container' className='BreakdownPie dc-col-sm-6'>
        <div
          ref='tooltip'
          className={`BreakdownPie-tooltip ${hcl(bgColor).l > 75 ? 'dark' : ''}`}
          style={{
            display: hovered ? 'block' : 'none',
            left: Math.max(0, Math.min(tooltipPos[0] + width/2  - tooltipWidth/2, width - tooltipWidth)),
            top:  tooltipPos[1] + height/2 - tooltipHeight/2,
            background: bgColor,
          }}
        >
          <span>{hovered ? hovered.data[nameField] : 'nothing is hovered'}</span>
          &nbsp;
          <span>{hovered ? weightFormat(hovered.data[weightField]) : ''}</span>
        </div>
        <svg width={width} height={height}>
          <g
            transform={`translate(${width/2},${height/2}) scale(${Math.min(1, (width - 70 * 2) / 200)})`}
          >
            { numTopN ? [
              /* We break really long labels into 2 lines -- right after the number (numTopN) */
                <text
                  key='part-1'
                  className='BreakdownPie-summary-caption'
                  textAnchor='middle'
                  dy='-2em'
                >{weightOfTopLabel.length <= len ? '' : weightOfTopLabel.substr(0, weightOfTopLabel.indexOf(numTopN) + String(numTopN).length)}</text>
              ,
                <text
                  key='part-2'
                  className='BreakdownPie-summary-caption'
                  textAnchor='middle'
                  dy='-1em'
                >{weightOfTopLabel.length <= len ? weightOfTopLabel : weightOfTopLabel.substr(weightOfTopLabel.indexOf(numTopN) + String(numTopN).length)}</text>
              ] : null
            }
            { percentTopN ? <text
                className='BreakdownPie-summary-percent'
                textAnchor='middle'
                dy='.6em'
              >{weightFormat(percentTopN)}</text> : null
            }
            { innerLabel ? <text
                className='BreakdownPie-summary-percent'
                textAnchor='middle'
                dy='.3em'
              >{innerLabel}</text> : null
            }
          </g>
          <g 
            onMouseLeave={() => onHighlight(null)}
            transform={`translate(${width/2},${height/2})`}
          >{
            pieSlices(this.props, this.state).map((d, i) => (
              <path
                key={i}
                stroke='white'
                fill={colorScale(d.data[nameField])}
                d={d.path}
                onClick={() => false /* TEMP: disable clicks / graphs */ && onSelect(d.data[nameField])}
                onMouseEnter={() => onHighlight(d.data[nameField])}
              />
            ))
          }</g>
        </svg>
      </div>
    )
  }
}

BreakdownPie.defaultProps = {
  weightOfTopLabel: 'Weight of Top Holdings Within Portfolio',
  splitInnerLabelIfLongerThan: 35
}

export default BreakdownPie



// WEBPACK FOOTER //
// ./src/BreakdownPie.js